
import React, { useState, useEffect } from 'react';

const MarksCalculator = () => {
  const [calculatorType, setCalculatorType] = useState('gpa');
  
  // Separate state for each calculator type to preserve data
  const [gpaData, setGpaData] = useState({
    subjects: [],
    selectedSemester: 1
  });
  const [cgpaData, setCgpaData] = useState([]);
  const [percentageData, setPercentageData] = useState({
    subjects: []
  });
  const [weightedData, setWeightedData] = useState({
    subjects: []
  });

  // Separate results for each calculator type
  const [calculatorResults, setCalculatorResults] = useState({
    gpa: null,
    cgpa: null,
    percentage: null,
    weighted: null
  });

  // Current result (derived from calculatorResults based on current type)
  const [result, setResult] = useState(null);

  // Base styles (keeping original design)
  const styles = {
    todoHeader: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      marginBottom: '16px'
    },
    welcomeTitle: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: '0 0 16px 0'
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '12px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      outline: 'none',
      backgroundColor: 'white',
      cursor: 'pointer',
      boxSizing: 'border-box'
    },
    addButton: {
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 20px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      margin: '8px 0'
    },
    deleteButton: {
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      padding: '8px 12px',
      fontSize: '14px',
      cursor: 'pointer',
      marginTop: '8px'
    },
    filterButton: {
      padding: '8px 16px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    filterActive: {
      backgroundColor: '#10b981',
      color: 'white'
    },
    filterInactive: {
      backgroundColor: '#f3f4f6',
      color: '#374151'
    },
    addTaskSection: {
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px'
    }
  };

  // Mobile-responsive styles that enhance the original design
  const calculatorStyles = {
    tabContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      gap: '8px',
      marginBottom: '24px',
      '@media (max-width: 640px)': {
        gridTemplateColumns: 'repeat(2, 1fr)'
      }
    },
    subjectGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '12px',
      marginBottom: '12px',
      '@media (max-width: 768px)': {
        gridTemplateColumns: '1fr'
      }
    },
    resultCard: {
      backgroundColor: '#ecfdf5',
      border: '2px solid #10b981',
      borderRadius: '8px',
      padding: '20px',
      textAlign: 'center',
      marginTop: '20px'
    },
    resultValue: {
      fontSize: '36px',
      fontWeight: 'bold',
      color: '#059669',
      marginBottom: '8px',
      '@media (max-width: 480px)': {
        fontSize: '28px'
      }
    },
    resultLabel: {
      fontSize: '18px',
      color: '#374151',
      fontWeight: '600',
      '@media (max-width: 480px)': {
        fontSize: '16px'
      }
    },
    infoBox: {
      backgroundColor: '#f0f9ff',
      border: '1px solid #0ea5e9',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '20px'
    },
    infoTitle: {
      fontWeight: '600',
      color: '#0c4a6e',
      marginBottom: '8px'
    },
    infoText: {
      color: '#0c4a6e',
      fontSize: '14px',
      lineHeight: '1.5'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '600',
      color: '#374151'
    },
    clearDataButton: {
      padding: '6px 12px',
      backgroundColor: '#dc2626',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px',
      marginLeft: '8px',
      '@media (max-width: 480px)': {
        marginLeft: '0',
        marginTop: '8px'
      }
    },
    // Mobile-specific responsive styles
    mobileTabContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '8px',
      marginBottom: '24px'
    },
    mobileSubjectGrid: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      marginBottom: '12px'
    },
    mobileHeaderContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      marginBottom: '16px'
    },
    mobileHeaderTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: '8px'
    },
    mobileHeaderActions: {
      display: 'flex',
      flexDirection: 'arrow',
      alignItems: 'flex-end',
      gap: '4px'
    }
  };

  // Check if mobile screen
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const gradeToGPA = {
    'A+': 4.0, 'A': 3.7, 'A-': 3.3,
    'B+': 3.0, 'B': 2.7, 'B-': 2.3,
    'C+': 2.0, 'C': 1.7, 'C-': 1.3,
    'D+': 1.0, 'D': 0.7, 'F': 0.0
  };

  // Helper function to save result for current calculator type
  const saveResult = (resultData) => {
    setResult(resultData);
    setCalculatorResults(prev => ({
      ...prev,
      [calculatorType]: resultData
    }));
  };

  // Clear all saved data function
  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all saved calculator data? This action cannot be undone.')) {
      try {
        // Reset all state (removing localStorage usage for artifacts)
        setGpaData({ subjects: [], selectedSemester: 1 });
        setCgpaData([]);
        setPercentageData({ subjects: [] });
        setWeightedData({ subjects: [] });
        setCalculatorType('gpa');
        setResult(null);
        setCalculatorResults({ gpa: null, cgpa: null, percentage: null, weighted: null });
        
        alert('All data cleared successfully!');
      } catch (error) {
        console.error('Error clearing data:', error);
        alert('Error clearing data. Please try again.');
      }
    }
  };

  // Helper functions to get current data based on calculator type
  const getCurrentSubjects = () => {
    switch (calculatorType) {
      case 'gpa': return gpaData.subjects;
      case 'percentage': return percentageData.subjects;
      case 'weighted': return weightedData.subjects;
      default: return [];
    }
  };

  const updateCurrentSubjects = (newSubjects) => {
    switch (calculatorType) {
      case 'gpa':
        setGpaData(prev => ({ ...prev, subjects: newSubjects }));
        break;
      case 'percentage':
        setPercentageData(prev => ({ ...prev, subjects: newSubjects }));
        break;
      case 'weighted':
        setWeightedData(prev => ({ ...prev, subjects: newSubjects }));
        break;
    }
  };

  const addSubject = () => {
    const currentSubjects = getCurrentSubjects();
    const newSubject = {
      id: Date.now(),
      name: '',
      credits: '',
      marks: '',
      grade: 'A+',
      weightage: ''
    };
    updateCurrentSubjects([...currentSubjects, newSubject]);
  };

  const updateSubject = (id, field, value) => {
    const currentSubjects = getCurrentSubjects();
    const updatedSubjects = currentSubjects.map(subject =>
      subject.id === id ? { ...subject, [field]: value } : subject
    );
    updateCurrentSubjects(updatedSubjects);
  };

  const removeSubject = (id) => {
    const currentSubjects = getCurrentSubjects();
    const filteredSubjects = currentSubjects.filter(subject => subject.id !== id);
    updateCurrentSubjects(filteredSubjects);
  };

  const calculateGPA = () => {
    const currentSubjects = getCurrentSubjects();
    if (currentSubjects.length === 0) return;

    let totalPoints = 0;
    let totalCredits = 0;

    currentSubjects.forEach(subject => {
      const credits = parseFloat(subject.credits) || 0;
      const gpa = gradeToGPA[subject.grade] || 0;
      
      totalPoints += gpa * credits;
      totalCredits += credits;
    });

    const gpa = totalCredits > 0 ? (totalPoints / totalCredits).toFixed(2) : 0;
    const resultData = { value: gpa, label: `GPA (Semester ${gpaData.selectedSemester})`, type: 'gpa' };
    saveResult(resultData);
  };

  const calculatePercentage = () => {
    const currentSubjects = getCurrentSubjects();
    if (currentSubjects.length === 0) return;

    const totalMarks = currentSubjects.reduce((sum, subject) => {
      return sum + (parseFloat(subject.marks) || 0);
    }, 0);

    const percentage = currentSubjects.length > 0 ? (totalMarks / currentSubjects.length).toFixed(2) : 0;
    const resultData = { value: percentage, label: 'Average Percentage', type: 'percentage' };
    saveResult(resultData);
  };

  const calculateWeighted = () => {
    const currentSubjects = getCurrentSubjects();
    if (currentSubjects.length === 0) return;

    let totalWeightedMarks = 0;
    let totalWeightage = 0;

    currentSubjects.forEach(subject => {
      const marks = parseFloat(subject.marks) || 0;
      const weightage = parseFloat(subject.weightage) || 0;
      
      totalWeightedMarks += marks * (weightage / 100);
      totalWeightage += weightage;
    });

    const weightedAverage = totalWeightage > 0 ? totalWeightedMarks.toFixed(2) : 0;
    const resultData = { 
      value: weightedAverage, 
      label: `Weighted Average (${totalWeightage}% total)`, 
      type: 'weighted' 
    };
    saveResult(resultData);
  };

  const addSemesterToCGPA = () => {
    if (!result || result.type !== 'gpa') return;
    
    const currentSubjects = getCurrentSubjects();
    const credits = currentSubjects.reduce((sum, subject) => sum + (parseFloat(subject.credits) || 0), 0);
    const newSemester = {
      id: Date.now(),
      semester: gpaData.selectedSemester,
      gpa: parseFloat(result.value),
      credits: credits,
      subjects: [...currentSubjects] // Store subjects for reference
    };
    
    // Remove existing semester data if updating
    const filteredCgpaData = cgpaData.filter(sem => sem.semester !== gpaData.selectedSemester);
    setCgpaData([...filteredCgpaData, newSemester].sort((a, b) => a.semester - b.semester));
  };

  const addNewSemesterSlot = () => {
    const maxSemester = Math.max(...cgpaData.map(sem => sem.semester), 0);
    setGpaData(prev => ({ 
      ...prev, 
      selectedSemester: maxSemester + 1,
      subjects: [] // Clear subjects when creating new semester
    }));
    // Clear result for new semester
    saveResult(null);
  };

  const loadSemesterData = (semesterNum) => {
    const semesterData = cgpaData.find(sem => sem.semester === semesterNum);
    if (semesterData) {
      setGpaData(prev => ({
        ...prev,
        selectedSemester: semesterNum,
        subjects: semesterData.subjects
      }));
      const resultData = { 
        value: semesterData.gpa, 
        label: `GPA (Semester ${semesterNum})`, 
        type: 'gpa' 
      };
      saveResult(resultData);
    } else {
      setGpaData(prev => ({
        ...prev,
        selectedSemester: semesterNum,
        subjects: []
      }));
      saveResult(null);
    }
  };

  const calculateCGPA = () => {
    if (cgpaData.length === 0) return;

    let totalPoints = 0;
    let totalCredits = 0;

    cgpaData.forEach(semester => {
      totalPoints += semester.gpa * semester.credits;
      totalCredits += semester.credits;
    });

    const cgpa = totalCredits > 0 ? (totalPoints / totalCredits).toFixed(2) : 0;
    const resultData = { value: cgpa, label: 'CGPA', type: 'cgpa' };
    saveResult(resultData);
  };

  const getGradeFromPercentage = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 85) return 'A';
    if (percentage >= 80) return 'A-';
    if (percentage >= 75) return 'B+';
    if (percentage >= 70) return 'B';
    if (percentage >= 65) return 'B-';
    if (percentage >= 60) return 'C+';
    if (percentage >= 55) return 'C';
    if (percentage >= 50) return 'C-';
    if (percentage >= 45) return 'D+';
    if (percentage >= 40) return 'D';
    return 'F';
  };

  const renderInfoBox = () => {
    const info = {
      gpa: {
        title: "GPA Calculator",
        text: "Calculate your Grade Point Average based on grades and credit hours. Select grades from A+ (4.0) to F (0.0). Data is automatically saved."
      },
      percentage: {
        title: "Percentage Calculator", 
        text: "Calculate your average percentage across subjects. Enter marks out of 100 for each subject. Data is automatically saved."
      },
      weighted: {
        title: "Weighted Average Calculator",
        text: "Calculate weighted average where each subject has different weightage. Perfect for complex grading schemes. Data is automatically saved."
      },
      cgpa: {
        title: "CGPA Calculator",
        text: "Calculate Cumulative Grade Point Average across multiple semesters. Add semester GPAs to calculate overall CGPA. Data is automatically saved."
      }
    };

    return (
      <div style={calculatorStyles.infoBox}>
        <div style={calculatorStyles.infoTitle}>{info[calculatorType].title}</div>
        <div style={calculatorStyles.infoText}>{info[calculatorType].text}</div>
      </div>
    );
  };

  return (
    <div style={styles.todoContainer}>
      <div style={styles.todoHeader}>
        <div style={isMobile ? calculatorStyles.mobileHeaderContainer : {display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
          <div style={isMobile ? calculatorStyles.mobileHeaderTop : {display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
            <h2 style={{...styles.welcomeTitle, fontSize: isMobile ? '24px' : '28px'}}>📊 Marks Calculator</h2>
            <div style={isMobile ? calculatorStyles.mobileHeaderActions : {display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span style={{fontSize: '12px', color: '#10b981'}}>✓ Auto-saved</span>
              <button onClick={clearAllData} style={calculatorStyles.clearDataButton}>
                🗑️ Clear All Data
              </button>
            </div>
          </div>
        </div>
        
        {renderInfoBox()}

        <div style={isMobile ? calculatorStyles.mobileTabContainer : calculatorStyles.tabContainer}>
          {[
            { id: 'gpa', label: 'GPA Calculator' },
            { id: 'cgpa', label: 'CGPA Calculator' },
            { id: 'percentage', label: 'Percentage' },
            { id: 'weighted', label: 'Weighted Average' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setCalculatorType(tab.id);
              }}
              style={{
                ...styles.filterButton,
                ...(calculatorType === tab.id ? styles.filterActive : styles.filterInactive),
                fontSize: isMobile ? '12px' : '14px',
                padding: isMobile ? '10px 12px' : '8px 16px'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {calculatorType === 'gpa' && (
          <>
            <div style={styles.addTaskSection}>
              <div style={isMobile ? calculatorStyles.mobileSubjectGrid : calculatorStyles.subjectGrid}>
                <div>
                  <label style={calculatorStyles.label}>Select Semester</label>
                  <select
                    value={gpaData.selectedSemester}
                    onChange={(e) => loadSemesterData(parseInt(e.target.value))}
                    style={styles.select}
                  >
                    {Array.from({length: Math.max(8, Math.max(...cgpaData.map(sem => sem.semester), 0) + 1)}, (_, i) => i + 1).map(sem => (
                      <option key={sem} value={sem}>
                        Semester {sem} {cgpaData.find(s => s.semester === sem) ? '(Saved)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{display: 'flex', alignItems: 'flex-end'}}>
                  <button onClick={addNewSemesterSlot} style={{...styles.addButton, backgroundColor: '#4335adff'}}>
                    ➕ New Semester
                  </button>
                </div>
              </div>
            </div>

            <button onClick={addSubject} style={styles.addButton}>
              ➕ Add Subject
            </button>

            {getCurrentSubjects().map(subject => (
              <div key={subject.id} style={styles.addTaskSection}>
                <div style={isMobile ? calculatorStyles.mobileSubjectGrid : calculatorStyles.subjectGrid}>
                  <div>
                    <label style={calculatorStyles.label}>Subject Name</label>
                    <input
                      type="text"
                      value={subject.name}
                      onChange={(e) => updateSubject(subject.id, 'name', e.target.value)}
                      placeholder="e.g., Mathematics"
                      style={styles.input}
                    />
                  </div>
                  <div>
                    <label style={calculatorStyles.label}>Credits</label>
                    <input
                      type="number"
                      value={subject.credits}
                      onChange={(e) => updateSubject(subject.id, 'credits', e.target.value)}
                      placeholder="3"
                      style={styles.input}
                      min="0"
                      step="0.5"
                    />
                  </div>
                  <div>
                    <label style={calculatorStyles.label}>Grade</label>
                    <select
                      value={subject.grade}
                      onChange={(e) => updateSubject(subject.id, 'grade', e.target.value)}
                      style={styles.select}
                    >
                      {Object.keys(gradeToGPA).map(grade => (
                        <option key={grade} value={grade}>
                          {grade} ({gradeToGPA[grade]})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  onClick={() => removeSubject(subject.id)}
                  style={styles.deleteButton}
                >
                  🗑️ Remove
                </button>
              </div>
            ))}

            {getCurrentSubjects().length > 0 && (
              <div>
                <button onClick={calculateGPA} style={styles.addButton}>
                  📊 Calculate GPA
                </button>

                {result && result.type === 'gpa' && (
                  <button
                    onClick={addSemesterToCGPA}
                    style={{...styles.addButton, backgroundColor: '#10b981'}}
                  >
                    💾 Save to CGPA
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {(calculatorType === 'percentage' || calculatorType === 'weighted') && (
          <>
            <button onClick={addSubject} style={styles.addButton}>
              ➕ Add Subject
            </button>

            {getCurrentSubjects().map(subject => (
              <div key={subject.id} style={styles.addTaskSection}>
                <div style={isMobile ? calculatorStyles.mobileSubjectGrid : calculatorStyles.subjectGrid}>
                  <div>
                    <label style={calculatorStyles.label}>Subject Name</label>
                    <input
                      type="text"
                      value={subject.name}
                      onChange={(e) => updateSubject(subject.id, 'name', e.target.value)}
                      placeholder="e.g., Mathematics"
                      style={styles.input}
                    />
                  </div>

                  <div>
                    <label style={calculatorStyles.label}>Marks (%)</label>
                    <input
                      type="number"
                      value={subject.marks}
                      onChange={(e) => updateSubject(subject.id, 'marks', e.target.value)}
                      placeholder="85"
                      style={styles.input}
                      min="0"
                      max="100"
                    />
                  </div>

                  {calculatorType === 'weighted' && (
                    <div>
                      <label style={calculatorStyles.label}>Weightage (%)</label>
                      <input
                        type="number"
                        value={subject.weightage}
                        onChange={(e) => updateSubject(subject.id, 'weightage', e.target.value)}
                        placeholder="25"
                        style={styles.input}
                        min="0"
                        max="100"
                      />
                    </div>
                  )}
                </div>

                <button
                  onClick={() => removeSubject(subject.id)}
                  style={styles.deleteButton}
                >
                  🗑️ Remove
                </button>
              </div>
            ))}

            {getCurrentSubjects().length > 0 && (
              <button
                onClick={() => {
                  if (calculatorType === 'percentage') calculatePercentage();
                  else if (calculatorType === 'weighted') calculateWeighted();
                }}
                style={styles.addButton}
              >
                📊 Calculate
              </button>
            )}
          </>
        )}

        {calculatorType === 'cgpa' && (
          <>
            <div style={styles.addTaskSection}>
              <h3 style={{marginBottom: '16px', color: '#374151'}}>Saved Semesters:</h3>
              {cgpaData.length === 0 && (
                <p style={{color: '#6b7280', fontStyle: 'italic'}}>
                  No semester data saved yet. Use the GPA Calculator to add semester GPAs.
                </p>
              )}
            </div>
            
            {cgpaData.map(semester => (
              <div key={semester.id} style={styles.addTaskSection}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? '12px' : '0'}}>
                  <div style={{flex: isMobile ? '1 1 100%' : '1'}}>
                    <strong>Semester {semester.semester}:</strong> GPA {semester.gpa} ({semester.credits} credits)
                    <div style={{fontSize: '12px', color: '#6b7280', marginTop: '4px'}}>
                      {semester.subjects.length} subjects: {semester.subjects.map(s => s.name).join(', ')}
                    </div>
                  </div>
                  <div style={{display: 'flex', gap: '8px', flexShrink: 0}}>
                    <button
                      onClick={() => {
                        setCalculatorType('gpa');
                        loadSemesterData(semester.semester);
                      }}
                      style={{...styles.filterButton, ...styles.filterInactive, margin: 0}}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => setCgpaData(cgpaData.filter(s => s.id !== semester.id))}
                      style={{...styles.deleteButton, marginTop: 0}}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {cgpaData.length > 0 && (
              <button onClick={calculateCGPA} style={styles.addButton}>
                📊 Calculate CGPA
              </button>
            )}
          </>
        )}

        {result && (
          <div style={calculatorStyles.resultCard}>
            <div style={{...calculatorStyles.resultValue, fontSize: isMobile ? '28px' : '36px'}}>
              {result.value}
            </div>
            <div style={{...calculatorStyles.resultLabel, fontSize: isMobile ? '16px' : '18px'}}>
              {result.label}
            </div>
            {result.type === 'percentage' && (
              <div style={{marginTop: '10px', fontSize: isMobile ? '14px' : '16px', color: '#374151'}}>
                Equivalent Grade: <strong>{getGradeFromPercentage(parseFloat(result.value))}</strong>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarksCalculator;